<script setup>
import { ref, computed } from "vue"
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import SVGIcon from '@/components/SVGIcon.vue';

const dataConnect = new DatastoreConnect();

const props = defineProps({
  field: {
    type: Object,
    default: () => ({}),
  },
  parent: {
    type: String,
    default: '',
  },
});

const files = ref([])
const uploading = ref(false)
const progress = ref({}) // name -> %
const selectFolder=ref(false);
const loading_status=ref('select');
var rootFolder = "";

const emit = defineEmits(['update:modelValue']);

const acceptFilter = computed(() => {
  const filter = props.field?.files_filter;
  return Array.isArray(filter) && filter.length > 0 ? filter.join(',') : null;
});

const select_msg = computed(() => {

  if (loading_status.value=='select'){
    if (files.value.length>0) return "";
    if (selectFolder.value) return "Select folder";
    return "Select files";
  }
  if (loading_status.value=='loading'){
    return "Uploading files";
  }
  if (loading_status.value=='loaded'){
    return "Uploading complete";
  }
  return "Uploading error";

});

/* =====================
   File selection
===================== */
function onFilesSelected(e) {
  loading_status.value='select';
  let selected = Array.from(e.target.files);

  // `accept` is ignored by browsers when selecting a folder (webkitdirectory), so re-filter here.
  const filter = props.field?.files_filter;
  if (Array.isArray(filter) && filter.length > 0) {
    const extensions = filter.map((ext) => String(ext).toLowerCase());
    selected = selected.filter((f) => extensions.some((ext) => f.name.toLowerCase().endsWith(ext)));
  }

  files.value = selected;
}

/* =====================
   Upload entry point
===================== */
async function uploadAll() {
  if (!files.value.length) return
  uploading.value = true
  loading_status.value='loading';

  // 1️⃣ Ask backend how to upload each file
  
  let ff= Array.from(files.value).map(f => ({
    name: f.name,
    path: f.webkitRelativePath,
    size: f.size,
    type: f.type,
  }))
  const resp = await dataConnect.uploadGetUrl({files:ff,folder:rootFolder});

  rootFolder=resp.result["folder"]
  const uploads  = resp.result["urls"];

  // 2️⃣ Upload each file
  for (const upload of uploads) {
    let file='';
    if (upload.path!=''){
      file = files.value.find(f => f.webkitRelativePath === upload.path)
    }
    if (!file) {
      file = files.value.find(f => f.name === upload.path)
    }
    if (!file) continue;

    progress.value[file.name] = 0

    if (upload.strategy === "single") {
      await uploadSingle(file, upload)
    } else {
      await uploadMultipart(file, upload)
    }

    progress.value[file.name] = 100
  }

  uploading.value = false
}

/* =====================
   Single PUT upload
===================== */
async function uploadSingle(file, plan) {
  try {
    let resp = await fetch(plan.putUrl, {method: "PUT",body: file});
    //console.log(resp);
    loading_status.value='loaded';
     emit('update:modelValue', rootFolder);
  }
  catch(ee) {
    console.log(ee);
    loading_status.value='error';
  }
  
}

/* =====================
   Multipart upload
===================== */
async function uploadMultipart(file, uploadObj) {
  const etags = []
  const totalParts = uploadObj.parts.length

  for (let i = 0; i < uploadObj.parts.length; i++) {
    const part = uploadObj.parts[i]
    const start = (part.partNumber - 1) * uploadObj.partSize
    const end = Math.min(start + uploadObj.partSize, file.size)

    const chunk = file.slice(start, end)

    const res = await fetch(part.url, {
      method: "PUT",
      body: chunk
    })

    const etag = res.headers.get("ETag")

    etags.push({
      PartNumber: part.partNumber,
      ETag: etag
    })

    progress.value[file.name] = Math.round(
      ((i + 1) / totalParts) * 100
    )
  }

  // 3️⃣ Notify backend to complete upload
  let params= {
    key: uploadObj.key,
    upload_id: uploadObj.uploadId,
    etag_list:etags,
    mode:'app-temp'
  }
  const resp = await dataConnect.completeSnapshotUploadMultipartUrl(params);
  if (resp.error) {
    loading_status.value='error';
  } else {
    loading_status.value='loaded';
    emit('update:modelValue', rootFolder);
  }

}
</script>

<template>
  <div class="uploader">
    <label v-if="props.field?.label || props.field?.description" class="label-spacing">
      {{ props.field.label || props.field.name }}
      <SVGIcon
        v-if="props.field.description"
        v-tooltip="props.field.description"
        :width="'20px'"
        :height="'20px'"
        :iconName="'info'"
        class="help-logo"
      />
    </label>

    <div class="top-row">
      <input v-if="selectFolder"
        type="file"
        multiple
        webkitdirectory
        directory
        :accept="acceptFilter"
        @change="onFilesSelected"
      />
      <input v-else
        type="file"
        multiple
        :accept="acceptFilter"
        @change="onFilesSelected"
      />
      <button class="button button-sm" @click="uploadAll" :disabled="uploading" v-if="files.length>0">
        Upload
      </button>
    </div>


    <div class="top-row select-row">
      <label style="width: 200px;">
        <input type="checkbox" v-model="selectFolder"/>
        Select folder
      </label>
      <div :class="loading_status">{{ select_msg }}</div>
    </div>

    
    <hr>
    <div class="file-list">
      <div v-for="file in files" :key="file.name" class="progress">
        <span>{{ file.name }}</span>
        <progress :value="progress[file.name] || 0" max="100"></progress>
        <span>{{ progress[file.name] || 0 }}%</span>
      </div>
    </div>

   

  </div>
</template>

<style scoped>
.label-spacing {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  margin-top: 6px;
  margin-bottom: 4px;
}
.top-row {
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
}
.select-row {
  justify-content: flex-start;
  gap: 10px;
}
.file-list {
  overflow-y: scroll;
  max-height: 300px;
  color: black;
}
.uploader {
  width: 100%;
}
.progress {
  display: flex;
  gap: 10px;
  align-items: center;
}
progress {
  width: 200px;
}

.error{
  color: rgb(139, 0, 0);
}
.loaded{
  color: rgb(2, 126, 4);
}
.loading{
  color: rgb(134, 164, 2);
}
.select{
  color: rgb(2, 68, 115);
}
</style>
